import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Shield, Star, ShoppingCart, Eye, Percent } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/components/ui/use-toast";
import { formatCurrency } from "@/utils/currencyUtils";
import { useState } from "react";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  vendors: {
    name: string;
    is_verified: boolean;
  };
  guarantee_eligible: boolean;
  images?: string[];
  rating?: number;
  rating_count?: number;
  discount_percentage?: number;
  original_price?: number;
  shipping_info?: {
    free_shipping: boolean;
    estimated_days: number;
  };
}

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleViewDetails = () => {
    navigate(`/shop/products/${product.id}`);
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      setIsAddingToCart(true);
      await addToCart(product);
      
      toast({
        title: "Added to cart",
        description: `${product.name} has been added to your cart`,
        variant: "default",
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: "Could not add item to cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="aspect-video bg-muted relative">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-muted to-muted-foreground/5" />
        )}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium text-primary shadow-sm">
          {formatCurrency(product.price, "GHS")}
        </div>
        {product.guarantee_eligible && (
          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-green-600 shadow-sm flex items-center gap-1">
            <Shield className="h-3 w-3" />
            Guarantee Eligible
          </div>
        )}
        {product.discount_percentage && product.discount_percentage > 0 && (
          <div className="absolute top-2 left-2 bg-destructive text-destructive-foreground px-3 py-1.5 rounded-full text-xs font-medium shadow-sm flex items-center gap-1">
            <Percent className="h-3 w-3" />
            {product.discount_percentage}% OFF
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
              {product.name}
            </h3>
            <p className="text-sm text-muted-foreground">{product.vendors.name}</p>
          </div>
          {product.vendors.is_verified && (
            <Shield className="h-5 w-5 text-primary" />
          )}
        </div>
        {(product.rating !== undefined && product.rating_count !== undefined) && (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="ml-1 text-sm font-medium">{product.rating.toFixed(1)}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              ({product.rating_count} reviews)
            </span>
          </div>
        )}
        <p className="text-muted-foreground mb-6 line-clamp-2">
          {product.description}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="default"
            className="w-full bg-primary hover:bg-primary/90 text-white"
            onClick={handleViewDetails}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </Button>
          <Button 
            variant="outline"
            className="w-full border-primary text-primary hover:bg-primary hover:text-white"
            onClick={handleAddToCart}
            disabled={isAddingToCart}
          >
            <ShoppingCart className="mr-2 h-4 w-4" />
            {isAddingToCart ? 'Adding...' : 'Add to Cart'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ProductCard;
